import React, { useState, useEffect } from 'react';

// Video optimization utilities
export const VideoOptimizer = {
  // Check if user has slow connection
  checkConnectionSpeed: () => {
    if ('connection' in navigator) {
      const connection = navigator.connection;
      return {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        isSlowConnection: connection.effectiveType === 'slow-2g' || 
                         connection.effectiveType === '2g' ||
                         connection.downlink < 1
      };
    }
    return { isSlowConnection: false };
  },

  // Get appropriate video quality based on connection
  getOptimalVideoSettings: () => {
    const connectionInfo = VideoOptimizer.checkConnectionSpeed();
    
    if (connectionInfo.isSlowConnection) {
      return {
        preload: 'none',
        poster: '/edulab-video-poster.jpg', // You can add a poster image
        showQualityOptions: true,
        defaultQuality: 'low'
      };
    }
    
    return {
      preload: 'metadata',
      poster: null,
      showQualityOptions: false,
      defaultQuality: 'high'
    };
  },

  // Lazy load video when it comes into viewport
  useIntersectionObserver: (ref, options = {}) => {
    const [isIntersecting, setIsIntersecting] = useState(false);
    
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
      },
      { threshold: 0.1, ...options }
    );
    
    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }
    
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [ref, options]);
    
    return isIntersecting;
  }
};

// Progressive loading component
export const ProgressiveVideoLoader = ({ src, children, ...props }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState({});

  useEffect(() => {
    const info = VideoOptimizer.checkConnectionSpeed();
    setConnectionInfo(info);
  }, []);

  const handleLoadStart = () => {
    setIsLoaded(false);
    setLoadError(false);
  };

  const handleCanPlay = () => {
    setIsLoaded(true);
  };

  const handleError = (e) => {
    console.error('ProgressiveVideoLoader error:', e);
    setLoadError(true);
  };

  const videoSettings = VideoOptimizer.getOptimalVideoSettings();

  return (
    <div className="progressive-video-loader">
      {!isLoaded && !loadError && (
        <div className="video-placeholder">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <p>Loading video...</p>
            {connectionInfo.isSlowConnection && (
              <p className="slow-connection-notice">
                Large video file detected. Loading may take a moment on slower connections.
              </p>
            )}
          </div>
        </div>
      )}
      
      {loadError && (
        <div className="video-error">
          <p>Unable to load video. Please check your connection and try again.</p>
          <button onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      )}
      
      {React.cloneElement(children, {
        ...props,
        src,
        onLoadStart: handleLoadStart,
        onCanPlay: handleCanPlay,
        onError: handleError,
        preload: videoSettings.preload,
        poster: videoSettings.poster,
        style: {
          ...props.style,
          display: isLoaded ? 'block' : 'none'
        }
      })}
    </div>
  );
};

export default VideoOptimizer;
